import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Trash2, Clock } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface SlotManagerProps {
  chargingSpotId: string;
}

const SlotManager = ({ chargingSpotId }: SlotManagerProps) => {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [startHour, setStartHour] = useState("08");
  const [startMinute, setStartMinute] = useState("00");
  const [endHour, setEndHour] = useState("12");
  const [endMinute, setEndMinute] = useState("00");

  const { data: slots, isLoading } = useQuery({
    queryKey: ["availability_slots", chargingSpotId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("availability_slots")
        .select("*")
        .eq("charging_spot_id", chargingSpotId)
        .order("start_time", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const addSlot = useMutation({
    mutationFn: async () => {
      if (!selectedDate) throw new Error("Bitte Datum wählen");

      const start = new Date(selectedDate);
      start.setHours(parseInt(startHour), parseInt(startMinute), 0, 0);

      const end = new Date(selectedDate);
      end.setHours(parseInt(endHour), parseInt(endMinute), 0, 0);

      if (end <= start) throw new Error("Endzeit muss nach Startzeit liegen");

      const { error } = await supabase.from("availability_slots").insert({
        charging_spot_id: chargingSpotId,
        start_time: start.toISOString(),
        end_time: end.toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["availability_slots", chargingSpotId] });
      toast.success("Zeitfenster hinzugefügt");
    },
    onError: (err: Error) => {
      toast.error(err.message.includes("overlap") ? "Zeitfenster überschneidet sich mit einem bestehenden" : err.message);
    },
  });

  const deleteSlot = useMutation({
    mutationFn: async (slotId: string) => {
      const { error } = await supabase.from("availability_slots").delete().eq("id", slotId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["availability_slots", chargingSpotId] });
      toast.success("Zeitfenster gelöscht");
    },
    onError: () => toast.error("Fehler beim Löschen"),
  });

  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
  const minutes = ["00", "15", "30", "45"];

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-foreground">Verfügbarkeit verwalten</h3>

      {/* Add new slot */}
      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <p className="text-sm font-medium text-foreground">Neues Zeitfenster</p>

        <div className="space-y-2">
          <Label>Datum</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !selectedDate && "text-muted-foreground")}>
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDate ? format(selectedDate, "PPP", { locale: de }) : "Datum wählen"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => date < new Date()}
                initialFocus
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Startzeit</Label>
            <div className="flex gap-1">
              <select value={startHour} onChange={(e) => setStartHour(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
                {hours.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
              <span className="flex items-center text-muted-foreground">:</span>
              <select value={startMinute} onChange={(e) => setStartMinute(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
                {minutes.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Endzeit</Label>
            <div className="flex gap-1">
              <select value={endHour} onChange={(e) => setEndHour(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
                {hours.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
              <span className="flex items-center text-muted-foreground">:</span>
              <select value={endMinute} onChange={(e) => setEndMinute(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
                {minutes.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
        </div>

        <Button onClick={() => addSlot.mutate()} disabled={!selectedDate || addSlot.isPending} className="w-full">
          {addSlot.isPending ? "Wird hinzugefügt…" : "Zeitfenster hinzufügen"}
        </Button>
      </div>

      {/* Existing slots */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">Bestehende Zeitfenster</p>
        {isLoading && <p className="text-sm text-muted-foreground">Lädt…</p>}
        {slots?.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Zeitfenster angelegt.</p>}
        {slots?.map((slot) => (
          <div key={slot.id} className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3">
            <div className="flex items-center gap-3">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div className="text-sm">
                <span className="font-medium">{format(new Date(slot.start_time), "dd.MM.yyyy", { locale: de })}</span>
                <span className="mx-2 text-muted-foreground">
                  {format(new Date(slot.start_time), "HH:mm")} – {format(new Date(slot.end_time), "HH:mm")}
                </span>
              </div>
              {slot.is_booked && (
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">Gebucht</span>
              )}
            </div>
            {!slot.is_booked && (
              <Button variant="ghost" size="icon" onClick={() => deleteSlot.mutate(slot.id)} disabled={deleteSlot.isPending}>
                <Trash2 className="h-4 w-4 text-muted-foreground" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SlotManager;
